import { Link } from "@tanstack/react-router";
import { MoreVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatNumber } from "@/lib/utils";
import type { ProjectOut } from "@/lib/api";

type ProjectColor = "brand" | "good" | "warn" | "purple";

function colorFromString(input: string | null): ProjectColor {
  if (!input) return "brand";
  const map: Record<string, ProjectColor> = {
    "#2563EB": "brand", "#10B981": "good", "#F97316": "warn", "#A855F7": "purple",
  };
  return map[input.toUpperCase()] ?? "brand";
}

const GRADIENT: Record<ProjectColor, string> = {
  brand: "from-brand-500 to-brand-700",
  good: "from-emerald-500 to-emerald-700",
  warn: "from-orange-500 to-orange-700",
  purple: "from-violet-500 to-violet-700",
};

export function ProjectCard({ p }: { p: ProjectOut }) {
  const color = colorFromString(p.color);
  const networksCount = (p.networks ?? []).length;
  const queriesCount = Object.values(p.network_queries ?? {}).filter((v) => v.trim()).length;
  const hasRecent = p.last_mention_at != null;

  return (
    <Link
      to="/monitor/$projectId"
      params={{ projectId: String(p.id) }}
      className="block bg-white dark:bg-ink-900 ring-1 ring-ink-100 dark:ring-ink-800 rounded-xl shadow-card overflow-hidden hover:shadow-pop hover:-translate-y-0.5 transition-all"
    >
      <div className={`h-1.5 bg-gradient-to-r ${GRADIENT[color]}`} />
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-1.5 flex-wrap">
            {(p.networks ?? []).slice(0, 4).map((n) => (
              <Badge key={n} variant="muted">{n}</Badge>
            ))}
            {(p.networks?.length ?? 0) > 4 && (
              <Badge variant="muted">+{(p.networks?.length ?? 0) - 4}</Badge>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => e.preventDefault()}
            className="text-ink-400 hover:text-ink-700 p-1"
            aria-label="Más opciones"
          >
            <MoreVertical size={14} />
          </button>
        </div>
        <h3 className="mt-3 text-base font-semibold text-ink-900 dark:text-white">{p.name}</h3>
        {p.description && <p className="text-xs text-ink-500 mt-0.5 line-clamp-2">{p.description}</p>}

        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-ink-50 dark:bg-ink-800 py-2">
            <div className="text-sm font-semibold text-ink-900 dark:text-white">{networksCount}</div>
            <div className="text-[10px] uppercase tracking-wide text-ink-500">Redes</div>
          </div>
          <div className="rounded-lg bg-ink-50 dark:bg-ink-800 py-2">
            <div className="text-sm font-semibold text-ink-900 dark:text-white">{formatNumber(p.mentions_count)}</div>
            <div className="text-[10px] uppercase tracking-wide text-ink-500">Menciones</div>
          </div>
          <div className="rounded-lg bg-ink-50 dark:bg-ink-800 py-2">
            <div className="text-sm font-semibold text-ink-900 dark:text-white">{queriesCount}</div>
            <div className="text-[10px] uppercase tracking-wide text-ink-500">Queries</div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-[11px] text-ink-500">
          <span className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${hasRecent ? "bg-good animate-pulse" : "bg-ink-300"}`} />
            {hasRecent ? `últ. ${new Date(p.last_mention_at!).toLocaleDateString("es-PE")}` : "sin actividad"}
          </span>
          <span>Creado {new Date(p.created_at).toLocaleDateString("es-PE")}</span>
        </div>
      </div>
    </Link>
  );
}
